import { ReactNode, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import type { CompleteSetupLevel } from '@kizenapps/engine';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from '@tanstack/react-query';
import { Typography } from '@kizen/kds/Typography';
import Loader from '__components/Kizen/Loader';
import Modal, { ModalBody } from '__components/Modals';
import { scrollbarCss } from '__app/spacing';
import { PLUGINS } from '__queries/query-keys';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import { PluginContext } from 'ts-components/Plugins/context';
import { PluginView } from 'ts-components/Plugins/PluginView';
import type { PluginViewState } from 'ts-components/Plugins/PluginView';
import { usePluginViewLoading } from 'ts-components/Plugins/usePluginViewLoading';
import { useRegisterActiveSetupSurface } from 'ts-components/Plugins/PluginContext';

export type SetupLevel = CompleteSetupLevel;

export const getSetupViewApiName = (
  config: MarketplacePlugin
): string | undefined => {
  const view = config.base_config?.setup_assistant?.view;

  return typeof view === 'string' && view.length > 0 ? view : undefined;
};

const SETUP_VIEW_MIN_HEIGHT = '400px';
const SETUP_VIEW_SCROLL_PADDING = '20px';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: ${SETUP_VIEW_MIN_HEIGHT};
  width: 100%;
  position: relative;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const AbsoluteLoader = styled(Loader)`
  background: transparent;
`;

const ModalScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-height: 0;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-padding: ${SETUP_VIEW_SCROLL_PADDING} 0;
  ${scrollbarCss({})}
`;

const SetupViewChrome = ({
  state,
  inline,
  children,
}: {
  state: PluginViewState;
  inline?: boolean;
  children: ReactNode;
}) => {
  const { loading } = usePluginViewLoading(state);

  return (
    <Body>
      {loading ? (
        <LoaderOverlay data-qa-id="plugin-setup-view-loading-overlay">
          <AbsoluteLoader loading />
        </LoaderOverlay>
      ) : null}
      {inline ? (
        children
      ) : (
        <ModalScrollArea data-qa-id="plugin-setup-view-scroll-area">
          {children}
        </ModalScrollArea>
      )}
    </Body>
  );
};

const ModalBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-height: ${SETUP_VIEW_MIN_HEIGHT};
  width: 100%;
  overflow: hidden;
`;

const ModalBodyLayout = styled(ModalBody)`
  display: flex;
  flex-direction: column;
`;

type SetupViewProps = {
  pluginApiName: string;
  viewApiName: string;
  level: SetupLevel;
  onComplete?: () => void;
  inline?: boolean;
};

export const SetupView = ({
  pluginApiName,
  viewApiName,
  level,
  onComplete,
  inline,
}: SetupViewProps) => {
  const { t } = useTranslation();
  const { routablePages } = useContext(PluginContext);

  useRegisterActiveSetupSurface(
    useMemo(
      () => ({ pluginApiName, level, onComplete }),
      [pluginApiName, level, onComplete]
    )
  );

  const viewResolved = useMemo(() => {
    return routablePages.some(
      (page) =>
        page.plugin_api_name === pluginApiName && page.api_name === viewApiName
    );
  }, [routablePages, pluginApiName, viewApiName]);

  const bootstrapFetching = useIsFetching({ queryKey: PLUGINS.LIST }) > 0;

  if (!viewResolved && bootstrapFetching) {
    return <Loader loading />;
  }

  return (
    <PluginView
      pluginApiName={pluginApiName}
      viewApiName={viewApiName}
      sizing={inline ? 'flex-content' : 'flex-fill'}
      fallback={
        <Typography>
          {t('The setup view for this app could not be loaded.')}
        </Typography>
      }
    >
      {(body, state) => (
        <SetupViewChrome state={state} inline={inline}>
          {body}
        </SetupViewChrome>
      )}
    </PluginView>
  );
};

type SetupViewModalProps = {
  onClose?: () => void;
  children: ReactNode;
};

export const SetupViewModal = ({ onClose, children }: SetupViewModalProps) => (
  <Modal
    show
    size="medium"
    onHide={onClose}
    frameless
    data-qa-id="plugin-setup-view-modal"
  >
    <ModalBodyLayout typeOfContent="plugin">
      <ModalBox>{children}</ModalBox>
    </ModalBodyLayout>
  </Modal>
);
